import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiAlertTriangle, FiSearch, FiTrash2, FiDatabase } from "react-icons/fi";
import axiosToken from "../../api/axiosClient";

const inputClass =
  "w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 font-nunito text-[14px] bg-white";

// Liste de ce que l'opération supprime (et détache)
function PlanSummary({ summary }) {
  if (!summary) return null;
  return (
    <div className="border border-red-200 bg-red-50 p-4 font-nunito text-[14px] text-red-900 space-y-2">
      <p className="font-semibold">
        {summary.total} élément(s) seront supprimés définitivement :
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
        {summary.deletes.map((d) => (
          <li key={d.table} className="flex justify-between gap-3">
            <span>{d.label}</span>
            <strong>{d.count}</strong>
          </li>
        ))}
      </ul>
      {summary.detaches.length > 0 && (
        <div className="pt-2 border-t border-red-200">
          <p className="font-semibold">Conservés mais détachés :</p>
          <ul>
            {summary.detaches.map((d) => (
              <li key={`${d.table}.${d.column}`}>
                {d.label} : {d.count} (le lien vers l'élément supprimé est retiré)
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Mot de passe + phrase à recopier, puis bouton d'exécution
function ConfirmBlock({ phrase, password, setPassword, confirmation, setConfirmation, onConfirm, loading, label }) {
  const normalize = (t) =>
    t.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim().toUpperCase();
  const ready = password && normalize(confirmation) === phrase;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="font-nunito text-[14px] text-text">
          Votre mot de passe
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            autoComplete="current-password"
          />
        </label>
        <label className="font-nunito text-[14px] text-text">
          Tapez <strong>{phrase}</strong>
          <input
            type="text"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            className={inputClass}
            autoComplete="off"
          />
        </label>
      </div>
      <button
        onClick={onConfirm}
        disabled={!ready || loading}
        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-nunito text-[14px] font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        <FiTrash2 size={14} />
        {loading ? "Suppression en cours..." : label}
      </button>
    </div>
  );
}

export default function SuperadminToolsPage() {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState("");

  // Suppression définitive
  const [type, setType] = useState("");
  const [ref, setRef] = useState("");
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgePassword, setPurgePassword] = useState("");
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purgeLoading, setPurgeLoading] = useState(false);

  // Vidage
  const [categories, setCategories] = useState([]);
  const [wipePreview, setWipePreview] = useState(null);
  const [wipePassword, setWipePassword] = useState("");
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wipeLoading, setWipeLoading] = useState(false);

  useEffect(() => {
    axiosToken
      .get("/admin-tools/config")
      .then((response) => {
        const data = response.data?.data;
        setConfig(data);
        setType(data?.types?.[0]?.key || "");
      })
      .catch((error) =>
        setConfigError(error?.response?.data?.error || "Outils indisponibles."),
      );
  }, []);

  const errorOf = (error, fallback) => error?.response?.data?.error || fallback;

  const previewPurge = async () => {
    setPurgePreview(null);
    try {
      const response = await axiosToken.get("/admin-tools/purge/preview", { params: { type, ref } });
      setPurgePreview(response.data?.data);
    } catch (error) {
      toast.error(errorOf(error, "Aperçu impossible."));
    }
  };

  const runPurge = async () => {
    setPurgeLoading(true);
    try {
      const response = await axiosToken.post("/admin-tools/purge", {
        type,
        ref,
        password: purgePassword,
        confirmation: purgeConfirm,
      });
      toast.success(response.data?.message || "Suppression effectuée.");
      setPurgePreview(null);
      setRef("");
      setPurgePassword("");
      setPurgeConfirm("");
    } catch (error) {
      toast.error(errorOf(error, "Suppression impossible."));
    } finally {
      setPurgeLoading(false);
    }
  };

  const toggleCategory = (key) => {
    setWipePreview(null);
    setCategories((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const previewWipe = async () => {
    setWipePreview(null);
    try {
      const response = await axiosToken.get("/admin-tools/wipe/preview", {
        params: { categories: categories.join(",") },
      });
      setWipePreview(response.data?.data);
    } catch (error) {
      toast.error(errorOf(error, "Aperçu impossible."));
    }
  };

  const runWipe = async () => {
    setWipeLoading(true);
    try {
      const response = await axiosToken.post("/admin-tools/wipe", {
        categories,
        password: wipePassword,
        confirmation: wipeConfirm,
      });
      toast.success(response.data?.message || "Base vidée.");
      setWipePreview(null);
      setCategories([]);
      setWipePassword("");
      setWipeConfirm("");
    } catch (error) {
      toast.error(errorOf(error, "Vidage impossible."));
    } finally {
      setWipeLoading(false);
    }
  };

  const currentType = config?.types?.find((t) => t.key === type);

  return (
    <div className="md:p-6 max-w-5xl">
      <div className="mb-6">
        <h2 className="font-poppins font-semibold text-[18px] text-title mb-2">
          Outils superadmin
        </h2>
        <div className="flex gap-3 items-start bg-orange-50 border border-orange-200 p-4 font-nunito text-[14px] text-orange-900">
          <FiAlertTriangle size={20} className="shrink-0 mt-0.5" />
          <p>
            Ces opérations sont <strong>définitives</strong>. Un aperçu indique tout ce qui
            sera supprimé, et une copie des données supprimées est enregistrée sur le
            serveur de l'API (dossier <code>backups</code>). Les rôles, la configuration
            du salon et votre propre compte ne sont jamais supprimés.
          </p>
        </div>
      </div>

      {configError && (
        <p className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 font-nunito text-[14px]">
          {configError}
        </p>
      )}

      {config && !config.enabled && (
        <p className="mb-6 bg-gray-100 border border-gray-300 px-4 py-3 font-nunito text-[14px]">
          Les outils superadmin sont désactivés sur ce serveur.
        </p>
      )}

      {config?.enabled && (
        <div className="space-y-8">
          {/* Suppression définitive */}
          <section className="bg-white border border-gray-200 p-6 space-y-4">
            <div>
              <h3 className="font-poppins font-semibold text-[16px] text-title flex items-center gap-2">
                <FiTrash2 className="text-red-600" />
                Supprimer définitivement un élément
              </h3>
              <p className="font-nunito text-[14px] text-text">
                L'élément est effacé de la base avec tout ce qui en dépend.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="font-nunito text-[14px] text-text">
                Type d'élément
                <select
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setPurgePreview(null);
                  }}
                  className={inputClass}
                >
                  {config.types.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="font-nunito text-[14px] text-text md:col-span-2">
                Référence ({currentType?.hint})
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={ref}
                    onChange={(e) => {
                      setRef(e.target.value);
                      setPurgePreview(null);
                    }}
                    className={inputClass}
                  />
                  <button
                    onClick={previewPurge}
                    disabled={!ref.trim()}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 font-nunito text-[14px] font-semibold hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                  >
                    <FiSearch size={14} />
                    Aperçu
                  </button>
                </div>
              </label>
            </div>
            {purgePreview && (
              <>
                <p className="font-poppins font-medium text-[15px] text-title">
                  {purgePreview.target.description}
                </p>
                <PlanSummary summary={purgePreview} />
                <ConfirmBlock
                  phrase={config.purgePhrase}
                  password={purgePassword}
                  setPassword={setPurgePassword}
                  confirmation={purgeConfirm}
                  setConfirmation={setPurgeConfirm}
                  onConfirm={runPurge}
                  loading={purgeLoading}
                  label="Supprimer définitivement"
                />
              </>
            )}
          </section>

          {/* Vider la base */}
          <section className="bg-white border border-gray-200 p-6 space-y-4">
            <div>
              <h3 className="font-poppins font-semibold text-[16px] text-title flex items-center gap-2">
                <FiDatabase className="text-red-600" />
                Vider la base de données
              </h3>
              <p className="font-nunito text-[14px] text-text">
                Choisissez les données à vider. Ce qui en dépend part aussi : l'aperçu le
                détaille avant toute suppression.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {config.categories.map((c) => (
                <label
                  key={c.key}
                  className={`flex items-start gap-3 p-3 border cursor-pointer ${
                    categories.includes(c.key) ? "border-red-400 bg-red-50" : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={categories.includes(c.key)}
                    onChange={() => toggleCategory(c.key)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block font-poppins font-medium text-[14px] text-subtitle">
                      {c.label}
                    </span>
                    <span className="block font-nunito text-[13px] text-text">{c.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setCategories(config.categories.map((c) => c.key));
                  setWipePreview(null);
                }}
                className="font-nunito text-[14px] text-red-700 hover:underline"
              >
                Tout sélectionner
              </button>
              <button
                onClick={() => {
                  setCategories([]);
                  setWipePreview(null);
                }}
                className="font-nunito text-[14px] text-text hover:underline"
              >
                Tout désélectionner
              </button>
            </div>
            <button
              onClick={previewWipe}
              disabled={!categories.length}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 font-nunito text-[14px] font-semibold hover:bg-gray-50 disabled:opacity-50"
            >
              <FiSearch size={14} />
              Aperçu du vidage
            </button>
            {wipePreview && (
              <>
                <PlanSummary summary={wipePreview} />
                <ConfirmBlock
                  phrase={config.wipePhrase}
                  password={wipePassword}
                  setPassword={setWipePassword}
                  confirmation={wipeConfirm}
                  setConfirmation={setWipeConfirm}
                  onConfirm={runWipe}
                  loading={wipeLoading}
                  label="Vider les données sélectionnées"
                />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
